/**
 * Stops the page shifting sideways when page scroll is locked.
 *
 * Three things lock scroll by hiding the document scrollbar: the inline cart
 * (`html.body--no-scroll`), the mobile menu (`body { position: fixed }`) and
 * the Klaviyo popup (`body { overflow: hidden }`, set by Klaviyo's own script).
 * On browsers with a classic, non-overlay scrollbar (Windows, or macOS with a
 * mouse attached) hiding the scrollbar widens the layout viewport by the
 * scrollbar width, so centred content jumps ~7px to the right and back again
 * when the lock is released.
 *
 * Rather than patching each lock (one of them is third-party), watch <html> and
 * <body> for attribute changes and, whenever the viewport has grown while a lock
 * is on, give <body> a matching padding-right. The gap is measured, not assumed,
 * so this is a no-op with overlay scrollbars or on mobile.
 */
export default function scrollLockGutter() {
  const html = document.documentElement;
  const body = document.body;

  let unlockedWidth = html.clientWidth;
  let applied = 0;

  const isLocked = () => {
    const h = getComputedStyle(html);
    const b = getComputedStyle(body);
    return h.overflowY === 'hidden' || b.overflowY === 'hidden' || b.position === 'fixed';
  };

  const setPadding = (px) => {
    if (px === applied) return;
    body.style.paddingRight = px ? `${px}px` : '';
    applied = px;
  };

  const sync = () => {
    if (!isLocked()) {
      setPadding(0);
      // html.clientWidth excludes the scrollbar; body padding does not affect it.
      unlockedWidth = html.clientWidth;
      return;
    }
    const gap = html.clientWidth - unlockedWidth;
    setPadding(gap > 0 ? gap : 0);
  };

  const observer = new MutationObserver(sync);
  observer.observe(html, { attributes: true, attributeFilter: ['class', 'style'] });
  observer.observe(body, { attributes: true, attributeFilter: ['class', 'style'] });
  window.addEventListener('resize', sync);

  sync();
}
